<template>
  <Html
    :lang="$i18n?.locale?.value?.split('_')[0] ? $i18n?.locale?.value?.split('_')[0] : 'en'"
  >
    <Body
      ref="bodyRef"
      class="js-isNotOverflowAuto"
      :class="[{
        'notOverflow': isNotOverflow,
        'lg:overflow-hidden': isBlockFirstScreen
      }]"
    >
      <template v-if="inited || !confGlobal.dev">
        <NuxtLayout
          name="layout"
        />

        <div
          class="modal-overlay customOverlay"
          :class="{ active: isShowOverlay }"
          @click="closeModal()"
        ></div>
      </template>

      <DelayHydration>
        <ClientOnly>
          <LazyHelpersInfoHeaderBanner v-if="infoHeaderBanner.message" />

          <LazyServiceCodes />
        </ClientOnly>
      </DelayHydration>

      <div id="credentional"></div>
    </Body>
  </Html>
</template>

<script setup>
const { confGlobal, confMain, confWidgets } = useConfig()
const { hook, $i18n } = useNuxtApp()
const { push: routerPush } = useRouter()
const { query, path } = useRoute()
const { useCustomCookie, setMetaTags } = useCustomTools()
const { hideWidget } = useWidgets()
const { getEntry } = useGeneral()
const { drawerOptions, popupNotOverflow, hidePubbleChat } = usePopups()

const {
  getLayout,
  infoHeaderBanner,
  currentPath,
  isNotOverflow,
  isShowOverlay,
  isBlockFirstScreen,
  closeModal
} = useHelpers()

const bodyRef = ref(false)

isNotOverflow.value = false

const inited = ref(true)

/**
* Build Watcher
* Credential and reload after deploy
*/
if (import.meta.client) {
  useBuildWatcher()
}

/**
* Meta Tags
*/
setMetaTags()

/**
* Hook for update data
*/
getLayout()

hook('page:start', () => {
  getLayout()

  document.querySelector('body').style.overflow = ''
    
  currentPath.value = useRoute().path
})

hook('page:finish', () => {
  const { to, path, hash } = useRoute()

  getLayout()

  useBreadcrumbs(to)

  currentPath.value = path

  setMetaTags()

  /*
  * Fix overflow
  */
  document.querySelector('body').style.overflow = ''
  document.body.classList.remove('notOverflowPanel')
  isNotOverflow.value = false

  /*
  * Scroll To Top
  */
  if (hash) {
    setTimeout(() => {
      scrollTo(null, document.querySelector(hash))
    }, 1)
  } else {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }

  /**
  * Block First Screen 
  */
  blockFirstScreen()
})

/**
* Block First Screen 
*/
const blockFirstScreen = () => { 
  if (hideWidget(confWidgets.blockFirstScreen) && useScroll(window).y.value === 0) {
    isBlockFirstScreen.value = true
  } else { 
    isBlockFirstScreen.value = false
  }
}

/**
* Watch Router
*/
watch(useRouter().currentRoute, () => {
  const { to, path, hash } = useRoute()

  getLayout()

  useBreadcrumbs(to)

  currentPath.value = path

  setMetaTags()

  /**
  * If change Url History, Close drawers and Dialogs
  */
  drawerOptions.value = {}

  document.querySelector('[data-pc-name="dialog"]')?.querySelector('[data-pc-name="pcclosebutton"]')?.click()

  /*
  * Scroll To Top
  */
  if (hash) {
    setTimeout(() => {
      scrollTo(null, document.querySelector(hash))
    }, 1)
  } else {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    })
  }
}, { deep: true })

/**
* Not Overflow For Drawers
*/
watch(drawerOptions, (newA) => {
  popupNotOverflow.value = Object.values(newA).some(value => typeof value === 'object' && value !== null && !Array.isArray(value) && Object.keys(value)?.length)

  document.querySelectorAll('.js-isNotOverflowAuto').forEach((item) => { 
    if (popupNotOverflow.value) {
      item.classList.add('p-overflow-hidden')
    } else {
      item.classList.remove('p-overflow-hidden')
    }
  })
}, { deep: true })

/**
* Caching status in Console
*/
const cachingMsg = () => {
  if (process.env.NODE_ENV === 'development') {
    if (useCustomCookie('disableCache').value) {
      console.log(
        '%cThe site works in the mode of disabled caching of large requests!%cTo enable caching of large requests, add the parameter%c ?disable-cache=false  %cto the url bar and refresh the page. Changes will be saved in Cookies.',
        'color: orange;',
        'color: orange;',
        'backgrount: #202124;border: dotted 1px #bdc1c6; color: #bdc1c6;',
        'color: orange;'
      )
    } else {
      console.log(
        '%cThe site works in the mode of enabled caching of large requests!%cTo disable caching of large requests, add the parameter%c ?disable-cache=true  %cto the url bar and refresh the page. Changes will be saved in Cookies.',
        'color: orange;',
        'color: orange;',
        'backgrount: #202124;border: dotted 1px #bdc1c6; color: #bdc1c6;',
        'color: orange;'
      )
    }
  }
}

/**
* MAIN LOGIC
*/
onMounted(async () => {
  cachingMsg()

  /**
  * Set Referral
  */
  if (query.referral) {
    useCustomCookie('referral', 60 * 60 * 24 * 30).value = query.referral
    routerPush(path)
  }

  /**
  * Maintenance Soft Mode
  */
  const { data: entryData, refresh: refreshEntryData } = getEntry()
  setInterval(() => {
    refreshEntryData()
  }, 60000)

  watch(entryData, async (newEntryData) => {
    if (newEntryData?._meta?.maintenance) {
      infoHeaderBanner.value.message = $i18n?.t('maintenanceSoftModeAgreeDescr', { siteName: confMain.siteName, telephone: confMain.phone, telephoneLink: confMain.phone.replace(/\D+/g, '') })
    } else {
      infoHeaderBanner.value.message = false
    }
  }, { deep: true })

  /*
  * Fix Tooltips Position (Left position)
  */
  const tooltipAction = () => {
    setTimeout(() => {
      const tooltip = document.querySelector('[data-pc-name="tooltip"]')

      if (tooltip) {
        const left = tooltip.style?.left?.replace('px', '') * 1
        if (left < 0) {
          tooltip.style.left = '0px'
        }
      }
    }, 1)
  }

  const attachTooltipListeners = () => {
    document.querySelectorAll('[data-pd-tooltip="true"]').forEach((el) => {
      el.onmouseenter = null
      el.onclick = null

      el.onmouseenter = () => {
        tooltipAction()
      }

      el.onclick = () => {
        tooltipAction()
      }
    })
  }

  attachTooltipListeners()
  const observer = new MutationObserver(attachTooltipListeners)
  observer.observe(document.body, { childList: true, subtree: true })

  /*
  * Fix [user-scalable="no"] is used in the <meta name="viewport"> element or the [maximum-scale] attribute is less than 5.
  */
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', () => {
      const viewportMeta = document.querySelector('meta[name=viewport]')
      if (!viewportMeta) return

      const height = window.visualViewport.height
      const totalHeight = window.innerHeight

      const isKeyboardVisible = height < totalHeight * 0.75

      viewportMeta.setAttribute('content', isKeyboardVisible
        ? 'width=device-width, initial-scale=1, maximum-scale=5'
        : 'width=device-width, initial-scale=1')
    })
  }

  /*
  * Close PubbleChat
  */
  document.addEventListener('mousedown', (event) => {
    const pubbleApp = document.querySelector('.pubble-app')
    const pubbleAppCTAs = document.querySelectorAll('.pubble-cw-contactbot')

    const clickedOutsidePubbleApp = pubbleApp && !pubbleApp.contains(event.target)

    const clickedOutsideAllCTAs = Array.from(pubbleAppCTAs).every(
      (cta) => !cta.contains(event.target)
    )

    if (clickedOutsidePubbleApp && clickedOutsideAllCTAs) {
      hidePubbleChat(event)
    }
  })
})

/**
* JS Heads
*/
useHead({
  script: [
    /**
    * Start page from 0 after reload
    */
    {
      key: 'prevent-script',
      children: `
          window.history.scrollRestoration = 'manual';
          setTimeout(() => {
            window.scrollTo(0, 0);
          }, 1);
        `
    }
  ]
})
</script>

<style lang="scss">
</style>
